<template>
  <CoreSelectionDrawer
    ref="selectionDrawerRef"
    has-search
    has-footer
    is-checked-variant
    title-truncate
    :value="isVisibleModal"
    :title="t('Специальность врача', options)"
    :placeholder="t('Название специальности', options)"
    :items="specialities"
    :selected-item="selectedSpeciality"
    :error="isError"
    :loading="isLoading"
    :is-back="isBack"
    attach=".vue-the-lpu-filters .v-application"
    @click-item="onClickSpeciality"
    @input="closeModal"
    @back="closeModal"
  >
    <template #append-header>
      <v-btn
        v-if="!isSpecialityDefault"
        color="primary"
        small
        text
        class="ml-auto"
        @click="clickResetBtn"
      >
        {{ t('Сбросить', options) }}
      </v-btn>
    </template>
    <template #footer>
      <slot
        name="footer"
        v-bind="{ selectedSpeciality }"
      >
        <div class="pa-2 elevation-8">
          <v-btn
            block
            depressed
            color="primary"
            :disabled="!filtersStore.hasUnsavedFilters"
            @click="$emit('speciality:selected', selectedSpeciality)"
          >
            {{ t('Применить', options) }}
          </v-btn>
        </div>
      </slot>
    </template>
  </CoreSelectionDrawer>
</template>

<script setup lang="ts">
import {
  computed, watch, ref, toRefs,
} from 'vue'
import I18NextVue from 'i18next'
import apiDoctorSpecialities from 'www/vue_components/common/TheFilters/lpu/api/apiDoctorSpecialities'
import useCommonStore from 'www/vue_components/common/TheFilters/lpu/stores/useCommonStore'
import useFiltersStore from 'www/vue_components/common/TheFilters/lpu/stores/useFiltersStore'
import CoreSelectionDrawer from 'src/core/CoreSelectionDrawer/CoreSelectionDrawer.vue'
import type { Filters } from 'www/vue_components/common/TheFilters/lpu/stores/useFiltersStore.types'
import type { ApiDoctorSpecialitiesParamsGet } from 'www/vue_components/common/TheFilters/lpu/api/apiDoctorSpecialities.types'
import { DEFAULT_DOCTOR_SPECIALITY } from 'www/vue_components/common/TheFilters/lpu/components/DoctorSpecialityFilters/constants'

const emit = defineEmits(['input', 'speciality:selected'])
const props = defineProps<{
  value: boolean
  isBack?: boolean
}>()

const { value: isVisibleModal } = toRefs(props)

const commonStore = useCommonStore()
const filtersStore = useFiltersStore()

const { t, language } = I18NextVue
const options = computed(() => ({ ns: language }))

const specialities = ref<NonNullable<Filters['doctorSpeciality']>[]>([])
const specialitiesCache: Map<string, NonNullable<Filters['doctorSpeciality']>[]> = new Map()
const selectedSpeciality = computed(() => filtersStore.unsavedFilters.doctorSpeciality)
const isSpecialityDefault = computed(() => !selectedSpeciality.value)
const isError = ref(false)
const isLoading = ref(false)
const selectionDrawerRef = ref<InstanceType<typeof CoreSelectionDrawer>>()

const loadSpecialities = async () => {
  isError.value = false
  isLoading.value = true

  try {
    const requestData: ApiDoctorSpecialitiesParamsGet = {
      town_id: commonStore.town.id,
      is_town_lpu_view: !filtersStore.unsavedFilters.lpuType,
      ...(filtersStore.unsavedFilters.lpuType && {
        lpu_type_id: filtersStore.unsavedFilters.lpuType.id,
        spec_id: filtersStore.unsavedFilters.doctorSpeciality?.id,
      }),
    }
    const cacheKey = JSON.stringify(requestData)

    if (specialitiesCache.has(cacheKey)) {
      specialities.value = specialitiesCache.get(cacheKey) || []
      return
    }

    const { data } = await apiDoctorSpecialities.get(requestData)

    specialities.value = [
      { ...DEFAULT_DOCTOR_SPECIALITY, name: t(DEFAULT_DOCTOR_SPECIALITY.name, options.value) },
      ...data,
    ]

    specialitiesCache.set(cacheKey, specialities.value)
  } catch (e) {
    isError.value = true
  } finally {
    isLoading.value = false
  }
}

watch(isVisibleModal, () => {
  if (isVisibleModal.value) {
    loadSpecialities()
  }
})

const restartModal = () => {
  selectionDrawerRef.value?.resetSearch()
  selectionDrawerRef.value?.scrollToActiveItem()
}
const closeModal = () => {
  emit('input', false)
  restartModal()
}

const clickResetBtn = () => {
  filtersStore.updateUnsavedFilters({ doctorSpeciality: undefined })
  restartModal()
}

const isDefaultSpeciality = (speciality: Filters['doctorSpeciality']) => !speciality || speciality.translit === DEFAULT_DOCTOR_SPECIALITY.translit
const getValidSpeciality = (speciality: Filters['doctorSpeciality']) => (!isDefaultSpeciality(speciality) ? speciality : undefined)
const onClickSpeciality = (speciality: Filters['doctorSpeciality']) => {
  filtersStore.updateUnsavedFilters({ doctorSpeciality: getValidSpeciality(speciality) })
}
</script>
